import { useEffect, useRef } from 'react';
import { gsap } from '../lib/gsapContext';
import Monolith from './Monolith';
import MagneticButton from './MagneticButton';

export default function Hero() {
  const root = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!root.current) return;
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
      tl.from('[data-hero-eyebrow]', { opacity: 0, y: 12, duration: 0.8 })
        .from(
          '[data-hero-word]',
          { opacity: 0, y: 40, duration: 1.1, stagger: 0.08 },
          '-=0.4'
        )
        .from(
          '[data-hero-sub]',
          { opacity: 0, y: 16, duration: 0.8 },
          '-=0.6'
        )
        .from(
          '[data-hero-cta]',
          { opacity: 0, y: 12, duration: 0.6, stagger: 0.1 },
          '-=0.4'
        )
        .from('[data-hero-corner]', { opacity: 0, duration: 1 }, 0.6);
    }, root);
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={root}
      className="grain relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-black px-6"
    >
      <div data-hero-corner>
        <Monolith />
      </div>

      <div
        aria-hidden
        className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(201,169,110,0.08),transparent_60%)]"
      />

      <div className="relative z-20 mx-auto flex max-w-5xl flex-col items-center text-center">
        <p
          data-hero-eyebrow
          className="label mb-8 inline-flex items-center gap-3"
        >
          <span className="h-px w-10 bg-gold" />
          Engineering-grade AI automation
          <span className="h-px w-10 bg-gold" />
        </p>

        <h1 className="font-display font-light text-white text-hero text-balance">
          {['Intelligence,', 'engineered', 'for'].map((w, i) => (
            <span
              key={i}
              data-hero-word
              className="mr-3 inline-block will-change-transform"
            >
              {w}
            </span>
          ))}
          <span
            data-hero-word
            className="mr-3 inline-block italic text-gold will-change-transform"
          >
            professional
          </span>
          <span
            data-hero-word
            className="inline-block italic text-gold will-change-transform"
          >
            services.
          </span>
        </h1>

        <p
          data-hero-sub
          className="mt-8 max-w-xl font-sans text-base leading-relaxed text-white/85"
        >
          We architect, build, and deploy production AI workflows for legal,
          accounting, recruitment, and consulting firms. Every engagement begins
          with a Technical Blueprint, not a pitch.
        </p>

        <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row">
          <span data-hero-cta>
            <MagneticButton to="/book">Book a Diagnostic</MagneticButton>
          </span>
          <span data-hero-cta>
            <MagneticButton to="/library" variant="ghost">
              Sample Blueprint
            </MagneticButton>
          </span>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 z-20 -translate-x-1/2">
        <div className="flex flex-col items-center gap-2">
          <span className="label">Scroll</span>
          <span className="block h-10 w-px animate-pulse bg-gradient-to-b from-gold to-transparent" />
        </div>
      </div>
    </section>
  );
}
